import { useEffect, useRef } from "react";

function DrumRollPicker({ values, selected, onSelect, appTheme }) {
  const itemHeight = 40;
  const visibleItems = 5;
  const containerHeight = itemHeight * visibleItems;
  const ref = useRef(null);
  const scrollTimeout = useRef(null);

  useEffect(() => {
    const index = values.indexOf(selected);
    if (ref.current && index !== -1) {
      ref.current.scrollTop = index * itemHeight;
    }
  }, [values, selected]);

  function handleScroll() {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    scrollTimeout.current = setTimeout(() => {
      if (!ref.current) return;

      const rawIndex = ref.current.scrollTop / itemHeight;
      const index = Math.round(rawIndex);
      const clamped = Math.max(0, Math.min(index, values.length - 1));

      ref.current.scrollTo({
        top: clamped * itemHeight,
        behavior: "smooth"
      });

      onSelect(values[clamped]);
    }, 100);
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "4px",
          right: "4px",
          height: `${itemHeight}px`,
          transform: "translateY(-50%)",
          background: appTheme.accentLight,
          borderRadius: "10px",
          border: appTheme.accentBorder,
          pointerEvents: "none",
          zIndex: 1
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: `${itemHeight * 2}px`,
          background: "linear-gradient(to bottom, rgba(255,255,255,0.96), transparent)",
          pointerEvents: "none",
          zIndex: 2
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: `${itemHeight * 2}px`,
          background: "linear-gradient(to top, rgba(255,255,255,0.96), transparent)",
          pointerEvents: "none",
          zIndex: 2
        }}
      />

      <div
        ref={ref}
        onScroll={handleScroll}
        style={{
          height: `${containerHeight}px`,
          overflowY: "scroll",
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <div style={{ height: `${itemHeight * 2}px`, flexShrink: 0 }} />

        {values.map((value) => (
          <div
            key={value}
            onClick={() => {
              onSelect(value);
              const index = values.indexOf(value);
              if (ref.current) {
                ref.current.scrollTo({
                  top: index * itemHeight,
                  behavior: "smooth"
                });
              }
            }}
            style={{
              height: `${itemHeight}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              scrollSnapAlign: "center",
              fontSize: "20px",
              fontWeight: value === selected ? "700" : "400",
              color: value === selected ? appTheme.primaryText : appTheme.mutedText,
              cursor: "pointer",
              transition: "color 0.15s ease, font-weight 0.15s ease",
              position: "relative",
              zIndex: 3,
              userSelect: "none",
              WebkitUserSelect: "none"
            }}
          >
            {value}
          </div>
        ))}

        <div style={{ height: `${itemHeight * 2}px`, flexShrink: 0 }} />
      </div>
    </div>
  );
}

export default DrumRollPicker;